import { useState } from 'react'

import type { BoardConnection, BoardElement, DisplayMode, Heads, Routing } from '../../api/boards'
import { canBeWrittenIn } from '../../api/library'
import { Button } from '../../ui/Control'
import { COLOURS } from './Toolbar'

/**
 * What the selected thing *is*, as a panel of its own beside the board.
 *
 * It used to be a third floating bar over the surface, and that was wrong twice over: a bar has room
 * for pictures and not for words, so every property was an icon somebody had to try; and it grew and
 * shrank as the selection changed, which moved the controls next to it under the pointer. A column
 * beside the board stays where it is, has room to name what each control does, and leaves the
 * surface to the two bars that are genuinely about *doing* something — putting things down, and
 * looking around.
 *
 * It is shown only where a board can be rearranged at all, so a phone never sees it.
 *
 * **Nothing here is a form.** Every control acts the moment it is used, on whatever is selected;
 * there is no Save anywhere, exactly as everywhere else in MyLib.
 */
export interface PropertiesProps {
  readonly selection: readonly BoardElement[]
  readonly line: BoardConnection | null
  readonly onColour: (colour: string | null) => void
  readonly onHighlight: (highlighted: boolean) => void
  readonly onPin: (pinned: boolean) => void
  /** Locks an element: it keeps its place, its size and its words until it is unlocked. */
  readonly onLock: (locked: boolean) => void
  /** How the picture of a card fills its tile. */
  readonly onFit: (fit: string) => void
  /** Selects how a PDF card is displayed. */
  readonly onDisplayMode: (mode: DisplayMode) => void
  /** Lets go of the selection, which is what closes this panel. */
  readonly onClose: () => void
  readonly onDepth: (toFront: boolean) => void
  readonly onTakeOff: () => void
  /** Opens what a card points at, in the area that owns it. */
  readonly onOpen: (element: BoardElement) => void
  /** Names a frame. The one thing on a board that carries a name of its own. */
  readonly onRename: (element: BoardElement, name: string) => void
  readonly onRouting: (routing: Routing) => void
  readonly onHeads: (heads: Heads) => void
  readonly onStraighten: () => void
  /** Fixes a line in place, so that clearing the board leaves it — the same protection an element has. */
  readonly onPinLine: (pinned: boolean) => void
}

/**
 * The three ways a line can run.
 *
 * All three, and that is worth saying because for a while it was two: `curved` existed in the type,
 * in the path and in the database, and was reachable from nowhere at all — which is the same as not
 * existing, and was reported as a missing feature twice before anybody noticed why.
 */
const ROUTINGS: readonly { readonly value: Routing; readonly label: string }[] = [
  { value: 'straight', label: 'Straight' },
  { value: 'elbow', label: 'Right angles' },
  { value: 'curved', label: 'Curved' },
]

/**
 * The three arrangements of arrow heads, each as its own control.
 *
 * Not one control that cycles through them. Cycling means reading the control to find out where you
 * are and pressing it until you arrive — which is fine for two states and guesswork for three.
 */
/**
 * How a card's picture fills its tile.
 *
 * *All of it* by default: a first page cropped to cover a square tile shows its top third and hides
 * where the words go. Covering is still worth having — a photograph reads better edge to edge.
 */
const FITS: readonly { readonly value: string; readonly label: string }[] = [
  { value: 'fit', label: 'All of it' },
  { value: 'fill', label: 'Fill the tile' },
]

/**
 * How a card shows its document — and there are two lists, because there are two kinds of document.
 *
 * A PDF is finished: its live forms are ways of looking at it. A marked-up text is on a board because
 * somebody is working on it, so its live forms are the set text and the editor. The same value
 * `document` therefore means "the content itself" in both lists, which is why the labels differ and
 * the values do not.
 */
const PDF_MODES: readonly { readonly value: DisplayMode; readonly label: string }[] = [
  { value: 'preview', label: 'Preview' },
  { value: 'document', label: 'Document' },
  { value: 'presentation', label: 'Presentation' },
]

const TEXT_MODES: readonly { readonly value: DisplayMode; readonly label: string }[] = [
  { value: 'preview', label: 'Preview' },
  { value: 'document', label: 'Read' },
  { value: 'writing', label: 'Write' },
]

/** Which list this card offers, or `null` for a document that has only the one, quiet picture. */
function modesFor(
  element: BoardElement,
): readonly { readonly value: DisplayMode; readonly label: string }[] | null {
  const document = element.document
  if (document === null) {
    return null
  }
  if (document.mediaType === 'application/pdf') {
    return PDF_MODES
  }
  return canBeWrittenIn(document) ? TEXT_MODES : null
}

const HEADS: readonly { readonly value: Heads; readonly label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'to', label: 'One' },
  { value: 'both', label: 'Both' },
]

export function Properties({
  selection,
  line,
  onColour,
  onHighlight,
  onPin,
  onLock,
  onFit,
  onDisplayMode,
  onClose,
  onDepth,
  onTakeOff,
  onOpen,
  onRename,
  onRouting,
  onHeads,
  onStraighten,
  onPinLine,
}: PropertiesProps) {
  const only = selection.length === 1 ? selection[0] : undefined
  const highlighted = selection.length > 0 && selection.every((one) => one.highlighted)
  const pinned = selection.length > 0 && selection.every((one) => one.pinned)
  const locked = selection.length > 0 && selection.every((one) => one.locked)

  if (selection.length === 0 && line === null) {
    return null
  }

  return (
    <aside className="board__aside" aria-label="What is selected">
      <header className="board__asideHead">
        <h3 className="board__asideTitle">{nameOf(selection, line)}</h3>
        {/* Escape does the same thing, and a control that only a key can reach is a control half the
            people who need it will never find. */}
        <Button tone="bare" icon="close" iconOnlyLabel="Let go of this" onClick={onClose} />
      </header>

      {only?.kind === 'frame' && <Naming frame={only} onRename={onRename} />}

      {only !== undefined && only.kind === 'card' && (
        <section className="board__group2">
          <Button
            tone="quiet"
            onClick={() => {
              onOpen(only)
            }}
          >
            {opening(only)}
          </Button>
          <p className="board__asideNote">
            The card stays where it is — this opens what it points at, in the area that keeps it.
          </p>
        </section>
      )}

      {only?.document !== undefined && only.document !== null && (
        <section className="board__group2">
          <h4 className="board__asideWhat">The picture</h4>
          <div className="board__row">
            {FITS.map((fit) => (
              <Button
                key={fit.value}
                tone={(only.fit ?? 'fit') === fit.value ? 'quiet' : 'bare'}
                aria-pressed={(only.fit ?? 'fit') === fit.value}
                onClick={() => {
                  onFit(fit.value)
                }}
              >
                {fit.label}
              </Button>
            ))}
          </div>
        </section>
      )}

      {only !== undefined && modesFor(only) !== null && (
        <section className="board__group2">
          <h4 className="board__asideWhat">Display</h4>
          <div className="board__row">
            {(modesFor(only) ?? []).map((mode) => {
              const selected = (only.displayMode ?? 'preview') === mode.value
              return (
                <Button
                  key={mode.value}
                  tone={selected ? 'quiet' : 'bare'}
                  aria-pressed={selected}
                  onClick={() => {
                    onDisplayMode(mode.value)
                  }}
                >
                  {mode.label}
                </Button>
              )
            })}
          </div>
          <p className="board__asideNote">
            {only.document?.mediaType === 'application/pdf'
              ? 'Presentation shows one page at a time and remembers the page reached on this card.'
              : 'Writing here saves itself and goes into the same history as writing in the library.'}
          </p>
        </section>
      )}

      {selection.length > 0 && (
        <>
          <section className="board__group2">
            <h4 className="board__asideWhat">Colour</h4>
            <div className="board__colours">
              {COLOURS.map((colour) => (
                <button
                  key={colour.id}
                  type="button"
                  className="board__colour"
                  data-colour={colour.id}
                  title={colour.label}
                  aria-label={colour.label}
                  aria-pressed={selection.every((one) => one.colour === colour.id)}
                  onClick={() => {
                    onColour(colour.id)
                  }}
                />
              ))}
              <button
                type="button"
                className="board__colour board__colour--none"
                title="No colour"
                aria-label="No colour"
                aria-pressed={selection.every((one) => one.colour === null)}
                onClick={() => {
                  onColour(null)
                }}
              />
            </div>
          </section>

          <section className="board__group2">
            <h4 className="board__asideWhat">Marking</h4>
            <label className="board__switch">
              <input
                type="checkbox"
                checked={highlighted}
                onChange={(event) => {
                  onHighlight(event.target.checked)
                }}
              />
              <span>Highlighted</span>
            </label>
          </section>

          <section className="board__group2">
            <h4 className="board__asideWhat">Keeping</h4>
            {/*
              **Two switches and not one.** They were one for a while, called "fixed in place", which
              answers "does this survive a clearing?" and reads as "can this be moved?" — two wishes
              that somebody will want separately: a card that must not be swept away but is
              rearranged constantly, and a frame that is finished and must not be nudged.
            */}
            <label className="board__switch">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(event) => {
                  onPin(event.target.checked)
                }}
              />
              <span>Permanent — survives clearing the board</span>
            </label>
            <label className="board__switch">
              <input
                type="checkbox"
                checked={locked}
                onChange={(event) => {
                  onLock(event.target.checked)
                }}
              />
              <span>Locked — cannot be moved, resized or written in</span>
            </label>
          </section>

          <section className="board__group2">
            <h4 className="board__asideWhat">Front to back</h4>
            <div className="board__row">
              <Button
                tone="quiet"
                icon="toFront"
                onClick={() => {
                  onDepth(true)
                }}
              >
                To the front
              </Button>
              <Button
                tone="quiet"
                icon="toBack"
                onClick={() => {
                  onDepth(false)
                }}
              >
                To the back
              </Button>
            </div>
          </section>
        </>
      )}

      {line !== null && (
        <>
          <section className="board__group2">
            <h4 className="board__asideWhat">How it runs</h4>
            <div className="board__row">
              {ROUTINGS.map((routing) => (
                <Button
                  key={routing.value}
                  tone={line.routing === routing.value ? 'quiet' : 'bare'}
                  aria-pressed={line.routing === routing.value}
                  onClick={() => {
                    onRouting(routing.value)
                  }}
                >
                  {routing.label}
                </Button>
              ))}
            </div>
          </section>

          <section className="board__group2">
            <h4 className="board__asideWhat">Arrow heads</h4>
            <div className="board__row">
              {HEADS.map((heads) => (
                <Button
                  key={heads.value}
                  tone={line.heads === heads.value ? 'quiet' : 'bare'}
                  aria-pressed={line.heads === heads.value}
                  onClick={() => {
                    onHeads(heads.value)
                  }}
                >
                  {heads.label}
                </Button>
              ))}
            </div>
          </section>

          <section className="board__group2">
            <h4 className="board__asideWhat">Corners</h4>
            {/*
              What the *whole line* has, and nothing about a single corner: taking one out happens at
              that corner, on the board, where it can be seen. A list of "Corner 1, Corner 2" here
              would name things nobody can point at and ask somebody to match them up by counting.
            */}
            {line.waypoints.length === 0 ? (
              <p className="board__asideNote">
                Drag one of the hollow handles — in the middle of each stretch — to bend the line
                there.
              </p>
            ) : (
              <>
                <Button tone="quiet" onClick={onStraighten}>
                  {line.waypoints.length === 1 ? 'Take the corner out' : 'Take all the corners out'}
                </Button>
                <p className="board__asideNote">
                  For one alone: double-click that corner. Dragging it back onto the line does the
                  same — a corner that bends nothing is not a corner.
                </p>
              </>
            )}
          </section>

          <section className="board__group2">
            <h4 className="board__asideWhat">Marking</h4>
            <label className="board__switch">
              <input
                type="checkbox"
                checked={line.pinned}
                onChange={(event) => {
                  onPinLine(event.target.checked)
                }}
              />
              <span>Fixed in place — survives clearing</span>
            </label>
          </section>
        </>
      )}

      <section className="board__group2">
        <Button tone="quiet" className="control--danger" onClick={onTakeOff}>
          {line !== null ? 'Remove this line' : takingOff(selection)}
        </Button>
        {selection.some((one) => one.kind === 'card') && (
          <p className="board__asideNote">
            What the cards point at is untouched and stays where it is kept.
          </p>
        )}
      </section>
    </aside>
  )
}

/**
 * A frame's name, typed where it is read.
 *
 * The only thing on a board that carries a name of its own: a card is called what it points at, and
 * a sticky note is called what it says. A frame says what a group of things *is*, and until now it
 * could only ever be called "Frame".
 */
function Naming({
  frame,
  onRename,
}: {
  readonly frame: BoardElement
  readonly onRename: (element: BoardElement, name: string) => void
}) {
  const [name, setName] = useState(frame.label ?? '')
  const [known, setKnown] = useState(frame.label ?? '')

  // What arrived from somewhere else while this was not being typed in — another tab, another device.
  if ((frame.label ?? '') !== known) {
    setKnown(frame.label ?? '')
    setName(frame.label ?? '')
  }

  const keep = () => {
    const wanted = name.trim()
    if (wanted !== '' && wanted !== (frame.label ?? '')) {
      onRename(frame, wanted)
    }
  }

  return (
    <section className="board__group2">
      <h4 className="board__asideWhat">Name</h4>
      <input
        className="board__field"
        value={name}
        aria-label="Name of this frame"
        placeholder="What belongs together here"
        onChange={(event) => {
          setName(event.target.value)
        }}
        onBlur={keep}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            keep()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            setName(frame.label ?? '')
            event.currentTarget.blur()
          }
        }}
      />
    </section>
  )
}

function nameOf(selection: readonly BoardElement[], line: BoardConnection | null): string {
  if (line !== null) {
    return 'Line'
  }
  if (selection.length > 1) {
    return `${selection.length} things`
  }
  switch (selection[0]?.kind) {
    case 'card':
      return 'Card'
    case 'sticky':
      return 'Sticky note'
    case 'text':
      return 'Text block'
    default:
      return 'Frame'
  }
}

/** What opening this card leads to, named after the area that keeps it. */
function opening(element: BoardElement): string {
  if (element.note !== null) {
    return 'Open on the notepad'
  }
  if (element.task !== null) {
    return 'Open in the task list'
  }
  return 'Open in the library'
}

function takingOff(selection: readonly BoardElement[]): string {
  return selection.length > 1 ? 'Take all of them off the board' : 'Take off the board'
}
